import { Suspense, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Form,
    Heading,
    LoadingPanel,
    Submit,
    toast,
    useDialogAction,
    useDialogClose,
} from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { apiClient } from '../../state';
import {
    type OpenAPISourceInputs,
    type OpenAPISourceType,
    OpenAPISpecSourceFields,
} from './OpenAPISpecSourceFields';
import { OpenAPIWaitForSpec } from './OpenAPIWaitForSpec';

export type OpenAPISpecEditDialogProps = {
    openAPISpec: api.OpenAPISpec;
    organizationId: string;
};

export function OpenAPISpecEditDialog(props: OpenAPISpecEditDialogProps) {
    const { openAPISpec, organizationId } = props;
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Edit OpenAPI specification</Heading>
                <Description>
                    Synchronize your specification from an URL or upload a new version from a file.
                </Description>
            </DialogHeader>
            <OpenAPIEditForm openAPISpec={openAPISpec} organizationId={organizationId} />
        </Dialog>
    );
}

interface OpenAPIEditFormInputs extends OpenAPISourceInputs {
    /**
     * Type of spec source.
     */
    type: OpenAPISourceType;
}

function OpenAPIEditForm(props: { openAPISpec: api.OpenAPISpec; organizationId: string }) {
    const { openAPISpec, organizationId } = props;
    const initialSourceType = openAPISpec.sourceURL ? 'url' : 'file';
    const [step, setStep] = useState<'source' | 'process'>('source');
    const doneRef = useRef<null | ((args: { close: boolean }) => void)>(null);
    const form = useForm<OpenAPIEditFormInputs>({
        defaultValues: {
            url: openAPISpec.sourceURL ?? '',
            text: null,
            type: initialSourceType,
        },
    });
    const closeDialog = useDialogClose();
    const type = form.watch('type');
    const [isPending, startDialogAction] = useDialogAction();
    const onSubmit: SubmitHandler<OpenAPIEditFormInputs> = async (data) => {
        const { type: sourceType } = data;
        assert(sourceType !== 'cli', 'CLI source should not be used to submit');
        const source: { url: string } | { text: string } = (() => {
            switch (sourceType) {
                case 'url': {
                    assert(data.url, 'url must be set');
                    return { url: data.url };
                }
                case 'file': {
                    assert(data.text, 'text must be set');
                    return { text: data.text.content };
                }
                default:
                    assertNever(sourceType);
            }
        })();
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await apiClient.openapi.createOrUpdateOpenApiSpecBySlug(
                    organizationId,
                    openAPISpec.slug,
                    { source }
                );
                analytics.logEvent({
                    type: 'openapi_spec_edit',
                    organization: organizationId,
                    fromSourceType: initialSourceType,
                    toSourceType: sourceType,
                    specSlug: openAPISpec.slug,
                });

                if ('text' in source) {
                    setStep('process');
                    await new Promise<void>((resolve) => {
                        doneRef.current = (args) => {
                            if (args.close) {
                                close().then(resolve);
                            } else {
                                resolve();
                            }
                        };
                    });
                } else {
                    await close();
                }
            }, 'Error while editing OpenAPI specification');
        });
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                {(() => {
                    switch (step) {
                        case 'source': {
                            return (
                                <OpenAPISpecSourceFields
                                    type={type}
                                    onTypeChange={(type) => form.setValue('type', type)}
                                    control={form.control}
                                    disabled={isPending}
                                    specSlug={openAPISpec.slug}
                                    organizationId={organizationId}
                                    onDone={closeDialog}
                                />
                            );
                        }
                        case 'process': {
                            return (
                                <Suspense fallback={<LoadingPanel />}>
                                    <OpenAPIWaitForSpec
                                        organizationId={organizationId}
                                        specSlug={openAPISpec.slug}
                                        errorAction={
                                            <Button
                                                label="Edit specification"
                                                onPress={() => {
                                                    setStep('source');
                                                }}
                                            />
                                        }
                                        onError={() => {
                                            assert(doneRef.current);
                                            doneRef.current({ close: false });
                                        }}
                                        onReady={() => {
                                            assert(doneRef.current);
                                            doneRef.current({ close: true });
                                        }}
                                    />
                                </Suspense>
                            );
                        }
                        default: {
                            assertNever(step);
                        }
                    }
                })()}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                {type !== 'cli' && <Submit label="Save" />}
            </DialogFooter>
        </Form>
    );
}
